import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  FiArrowRight,
  FiAward,
  FiChevronLeft,
  FiChevronRight,
  FiGift,
  FiStar,
  FiUsers,
  FiZap,
} from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { bonusPoolApi } from '../../services/bonusPoolApi';
import { parseDateInput } from '../../utils/formatters';

const SLIDE_INTERVAL_MS = 6000;
const MASK_STARS = '*****';

const maskName = (value) => {
  if (!value) return `U${MASK_STARS}`;
  const clean = String(value).trim();
  if (!clean) return `U${MASK_STARS}`;
  return `${clean[0]}${MASK_STARS}`;
};

const formatTimestamp = (value) => {
  if (!value) return '-';
  const date = parseDateInput(value);
  if (!date) return '-';
  return `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`;
};

const formatAmount = (value) => {
  if (typeof value !== 'number') return '-';
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
  return `${formatted}U`;
};

const getLatestEndedRound = (rounds) => {
  const ended = rounds.filter((round) => round.status === 'ended');
  if (!ended.length) {
    return null;
  }
  return ended.sort((a, b) => {
    const timeA = parseDateInput(a.drawTime || a.endTime || 0)?.getTime() || 0;
    const timeB = parseDateInput(b.drawTime || b.endTime || 0)?.getTime() || 0;
    return timeB - timeA;
  })[0];
};

const getSuccess = (response) => {
  const responseCode = response?.data?.code;
  return response?.ok && (responseCode === 'SUCCESS' || responseCode === 0 || responseCode === '0');
};

function TicketGuideModal({ open, onClose }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const inviteLink = summary?.inviteCode
    ? `${window.location.origin}/bonus-pool/invite/${summary.inviteCode}`
    : '';

  const loadSummary = useCallback(async () => {
    if (!isAuthenticated) {
      setSummary(null);
      return;
    }
    setLoading(true);
    const res = await bonusPoolApi.getInviteSummary();
    if (getSuccess(res)) {
      setSummary(res.data?.data || null);
    } else {
      setSummary(null);
    }
    setLoading(false);
  }, [isAuthenticated]);

  useEffect(() => {
    if (!open) return;
    loadSummary();
  }, [open, loadSummary]);

  const handleCopy = useCallback(async () => {
    if (!inviteLink) return;
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch {
      setCopySuccess(false);
    }
  }, [inviteLink]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 px-4">
      <div className="w-full max-w-3xl rounded-3xl border border-glass-border bg-surface/95 p-6 text-slate-100 shadow-[0_30px_80px_rgba(0,0,0,0.45)]">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-slate-500">
              {t('bonusPool.ticketGuideTag')}
            </div>
            <div className="mt-2 text-2xl font-semibold text-white">
              {t('bonusPool.ticketGuideTitle')}
            </div>
            <div className="mt-1 text-sm text-slate-300">
              {t('bonusPool.ticketGuideSubtitle')}
            </div>
          </div>
          <button
            className="rounded-full border border-glass-border p-2 text-slate-300 hover:text-white"
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        {!isAuthenticated ? (
          <div className="mt-6 rounded-2xl border border-glass-border bg-surface/70 p-5 text-sm text-slate-300">
            <div className="text-base font-semibold text-white">
              {t('bonusPool.ticketGuideLoginTitle')}
            </div>
            <div className="mt-2">{t('bonusPool.ticketGuideLoginDesc')}</div>
            <button
              onClick={() => navigate('/login')}
              className="mt-4 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-accent-strong to-accent px-4 py-2 text-sm font-semibold text-white"
            >
              <FiArrowRight className="h-4 w-4" />
              {t('bonusPool.ticketGuideLoginAction')}
            </button>
          </div>
        ) : (
          <div className="mt-6 grid gap-4 lg:grid-cols-2">
            <div className="rounded-2xl border border-glass-border bg-surface/70 p-5">
              <div className="flex items-center gap-2 text-sm font-semibold text-white">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-accent-warm/20 text-accent-warm">
                  <FiStar className="h-5 w-5" />
                </div>
                {t('bonusPool.ticketGuideMembershipTitle')}
              </div>
              <div className="mt-3 text-sm text-slate-300">
                {user?.tier_name === 'pro' 
                  ? t('bonusPool.ticketGuideProDesc', '您已是 Pro 会员，可直接进入抽奖。')
                  : t('bonusPool.ticketGuideMembershipDesc')
                }
              </div>
              {user?.tier_name === 'pro' ? (
                <button
                  onClick={() => {
                    onClose?.();
                    navigate('/bonus-pool');
                  }}
                  className="mt-4 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-accent-warm to-accent-soft px-4 py-2 text-sm font-semibold text-slate-900"
                >
                  {t('bonusPool.ticketGuideEnterDraw', '进入抽奖')}
                  <FiArrowRight className="h-4 w-4" />
                </button>
              ) : (
                <button
                  onClick={() => {
                    onClose?.();
                    window.dispatchEvent(
                      new CustomEvent('membership-required', {
                        detail: { currentTier: user?.tier_name || 'free' },
                      })
                    );
                  }}
                  className="mt-4 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-accent-warm to-accent-soft px-4 py-2 text-sm font-semibold text-slate-900"
                >
                  {t('bonusPool.ticketGuideMembershipAction')}
                  <FiArrowRight className="h-4 w-4" />
                </button>
              )}
            </div>

            <div className="rounded-2xl border border-glass-border bg-surface/70 p-5">
              <div className="flex items-center gap-2 text-sm font-semibold text-white">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-accent-strong/20 text-accent-soft">
                  <FiUsers className="h-5 w-5" />
                </div>
                {t('bonusPool.ticketGuideInviteTitle')}
              </div>
              <div className="mt-3 text-sm text-slate-300">
                {t('bonusPool.ticketGuideInviteDesc')}
              </div>

              <div className="mt-4 rounded-xl border border-glass-border bg-surface/80 p-3">
                <div className="flex items-center justify-between text-xs text-slate-400">
                  <span>{t('bonusPool.ticketGuideInviteProgress')}</span>
                  <span className="text-white">
                    {summary?.qualifiedInvites ?? 0}/{summary?.inviteRequired ?? 5}
                  </span>
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <div className="flex-1 rounded-lg border border-white/10 bg-surface/70 px-3 py-2 text-xs text-slate-200">
                    {inviteLink || t('bonusPool.ticketGuideInviteMissing')}
                  </div>
                  <button
                    onClick={handleCopy}
                    className="rounded-lg border border-glass-border px-3 py-2 text-xs text-slate-200 hover:bg-glass-hover"
                    disabled={!inviteLink}
                  >
                    {copySuccess ? t('bonusPool.ticketGuideInviteCopied') : t('bonusPool.ticketGuideInviteCopy')}
                  </button>
                </div>
                {loading && (
                  <div className="mt-2 text-xs text-slate-400">{t('bonusPool.ticketGuideInviteLoading')}</div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="mt-6 flex justify-end">
          <button
            className="rounded-xl border border-glass-border px-4 py-2 text-sm text-slate-200 hover:bg-glass-hover"
            onClick={onClose}
          >
            {t('bonusPool.claimClose')}
          </button>
        </div>
      </div>
    </div>
  );
}

export function BonusPoolPromoBanner() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [slides, setSlides] = useState([]);
  const [roundMeta, setRoundMeta] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [guideOpen, setGuideOpen] = useState(false);

  const tiers = useMemo(
    () => [
      {
        key: 'first',
        label: t('bonusPool.winnerTierFirst'),
        tone: 'text-amber-200',
        badge: 'border-amber-300/30 bg-amber-300/15 text-amber-200',
        icon: FiAward,
      },
      {
        key: 'second',
        label: t('bonusPool.winnerTierSecond'),
        tone: 'text-slate-200',
        badge: 'border-slate-400/30 bg-slate-400/15 text-slate-200',
        icon: FiStar,
      },
      {
        key: 'third',
        label: t('bonusPool.winnerTierThird'),
        tone: 'text-amber-100',
        badge: 'border-amber-200/20 bg-amber-200/10 text-amber-100',
        icon: FiGift,
      },
    ],
    [t]
  );

  const loadBanner = useCallback(async () => {
    setLoading(true);
    const roundsRes = await bonusPoolApi.getRounds();
    if (!getSuccess(roundsRes)) {
      setSlides([]);
      setRoundMeta(null);
      setLoading(false);
      return;
    }

    const rounds = roundsRes.data?.data?.rounds || [];
    const latestRound = getLatestEndedRound(rounds);
    if (!latestRound) {
      setSlides([
        {
          title: t('bonusPool.bannerNoResultTitle'),
          subtitle: t('bonusPool.bannerNoResultSubtitle'),
          winners: [],
          tierLabel: t('bonusPool.bannerNoResultTag'),
          badge: 'border-accent-strong/30 bg-accent-strong/10 text-accent-soft',
          tone: 'text-accent-soft',
          icon: FiZap,
        },
      ]);
      setRoundMeta(null);
      setLoading(false);
      return;
    }

    const detailRes = await bonusPoolApi.getRoundDetail(latestRound.id);
    if (!getSuccess(detailRes)) {
      setSlides([]);
      setRoundMeta(null);
      setLoading(false);
      return;
    }

    const detail = detailRes.data?.data || {};
    const winners = detail.winners || {};
    const newSlides = tiers.map((tier) => {
      const list = winners[tier.key] || [];
        const entries = list.slice(0, 2).map((item) => ({
          name: maskName(item.displayName),
          amount: formatAmount(item.amount),
          ticket: item.ticketCode,
        }));
      return {
        title: t('bonusPool.bannerTierTitle', { tier: tier.label }),
        subtitle: t('bonusPool.bannerTierSubtitle', { round: detail.id }),
        winners: entries,
        tierLabel: tier.label,
        badge: tier.badge,
        tone: tier.tone,
        icon: tier.icon,
      };
    });

    setSlides(newSlides);
    setRoundMeta({
      id: detail.id,
      poolAmount: detail.poolAmount,
      drawTime: detail.drawTime,
    });
    setLoading(false);
  }, [t, tiers]);

  useEffect(() => {
    loadBanner();
  }, [loadBanner]);

  useEffect(() => {
    if (slides.length <= 1) return;
    const timer = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % slides.length);
    }, SLIDE_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [slides.length]);

  const currentSlide = slides[activeIndex];

  const handlePrev = () => {
    if (slides.length <= 1) return;
    setActiveIndex((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const handleNext = () => {
    if (slides.length <= 1) return;
    setActiveIndex((prev) => (prev + 1) % slides.length);
  };

  return (
    <>
      <section className="relative overflow-hidden rounded-2xl border border-glass-border bg-glass px-6 py-5 shadow-[0_18px_30px_rgba(8,12,18,0.35)]">
        <div
          className="absolute -left-10 -top-14 h-48 w-48 rounded-full bg-accent-strong/20 blur-3xl"
          aria-hidden="true"
        />
        <div
          className="absolute -bottom-16 right-0 h-52 w-52 rounded-full bg-accent-warm/20 blur-3xl"
          aria-hidden="true"
        />

        <div className="relative z-10 flex flex-col gap-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="inline-flex items-center gap-2 rounded-full border border-glass-border bg-surface/70 px-3 py-1 text-xs uppercase tracking-[0.25em] text-slate-400">
              <FiGift className="h-3.5 w-3.5 text-accent-warm" />
              {t('bonusPool.bannerTag')}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setGuideOpen(true);
                }}
                className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-accent-warm to-accent-soft px-4 py-2 text-xs font-semibold text-slate-900 shadow-[0_12px_20px_rgba(8,12,18,0.35)] transition-transform hover:scale-[1.02]"
              >
                {t('bonusPool.bannerCta')}
                <FiArrowRight className="h-4 w-4" />
              </button>
              <button
                onClick={() => navigate('/bonus-pool/rounds')}
                className="inline-flex items-center gap-2 rounded-xl border border-glass-border bg-surface/70 px-4 py-2 text-xs text-slate-200 hover:bg-glass-hover"
              >
                {t('bonusPool.bannerGoRounds')}
              </button>
            </div>
          </div>

          {loading ? (
            <div className="rounded-2xl border border-glass-border bg-surface/60 p-5 text-sm text-slate-300">
              {t('bonusPool.bannerLoading')}
            </div>
          ) : currentSlide ? (
            <div className="grid gap-6 lg:grid-cols-[1.2fr,0.8fr]">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className={`flex h-9 w-9 items-center justify-center rounded-xl border ${currentSlide.badge}`}>
                    <currentSlide.icon className={`h-5 w-5 ${currentSlide.tone}`} />
                  </div>
                  <div>
                    <div className="text-lg font-semibold text-white">{currentSlide.title}</div>
                    <div className="text-sm text-slate-400">{currentSlide.subtitle}</div>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  {currentSlide.winners?.length ? (
                    currentSlide.winners.map((winner, idx) => (
                      <div
                        key={`${winner.name}-${idx}`}
                        className="min-w-[160px] rounded-xl border border-white/10 bg-surface/70 px-3 py-2 text-sm text-slate-200"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <span className="font-semibold">{winner.name}</span>
                          <span className="text-amber-200 font-semibold">{winner.amount}</span>
                        </div>
                        <div className="mt-1 text-xs text-slate-400">{winner.ticket || '-'}</div>
                      </div>
                    ))
                  ) : (
                    <div className="rounded-xl border border-white/10 bg-surface/70 px-4 py-3 text-sm text-slate-300">
                      {t('bonusPool.bannerEmpty')}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-col justify-between gap-4 rounded-2xl border border-glass-border bg-surface/70 p-5">
                <div>
                  <div className="text-xs uppercase tracking-[0.25em] text-slate-500">
                    {t('bonusPool.bannerPoolTag')}
                  </div>
                  <div className="mt-2 text-3xl font-semibold text-white">
                    {typeof roundMeta?.poolAmount === 'number'
                      ? formatAmount(roundMeta.poolAmount)
                      : '-'}
                  </div>
                  <div className="mt-1 text-xs text-slate-400">
                    {roundMeta?.id || '-'} · {formatTimestamp(roundMeta?.drawTime)}
                  </div>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <button
                    onClick={handlePrev}
                    className="rounded-full border border-glass-border p-2 text-slate-300 hover:text-white"
                    disabled={slides.length <= 1}
                  >
                    <FiChevronLeft className="h-4 w-4" />
                  </button>
                  <div className="flex items-center gap-1">
                    {slides.map((_, idx) => (
                      <span
                        key={`banner-dot-${idx}`}
                        className={`h-1.5 w-6 rounded-full transition-all ${
                          idx === activeIndex ? 'bg-accent-warm' : 'bg-white/10'
                        }`}
                      />
                    ))}
                  </div>
                  <button
                    onClick={handleNext}
                    className="rounded-full border border-glass-border p-2 text-slate-300 hover:text-white"
                    disabled={slides.length <= 1}
                  >
                    <FiChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="rounded-2xl border border-glass-border bg-surface/60 p-5 text-sm text-slate-300">
              {t('bonusPool.bannerEmpty')}
            </div>
          )}
        </div>
      </section>

      <TicketGuideModal open={guideOpen} onClose={() => setGuideOpen(false)} />
    </>
  );
}
